import _, { Dictionary } from 'lodash';
import moment from 'moment';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { Box, Grid, IconButton, Stack } from '@mui/material';

import { formatDate, queryParams } from '@shared/commonFunctions';
import { SERVICES } from '@shared/constants/apiConfig';
import { dateFormat, dateFormatWithTime, timeFormat } from '@shared/constants/formats';
import { TextInput } from '@shared/inputs/TextInput';
import { ILendingLoader } from '@shared/components/IlendingLoader';
import { ArrowUpwardIcon } from '@styles/ContactManager/HeaderStyles';
import { ILendingLoaderWrapper, LenderCommentInput } from '@styles/ContactManager/DealCalculator/LendersStyles';
import { Modal } from '@shared/components/modals/Modal';
import { StyleCommentchat } from '@styles/StyledChatBox';

import { useUserAccount } from '@shared/hooks/useUserAccount';
import { IComment } from '../interfaces';
import { sendLenderCommnet } from '../queries';

interface ILenderCommentsModalProps {
  lenderModalPosition: number;
  onClose(): void;
}

const renderTheirComment = (data: IComment, isDateShown?: boolean) => {
  return (
    <Box className="main-chat-wrapper-talking">
      <Box>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          {isDateShown && (
            <Grid item xs={3}>
              <>
                <Box className="chat-date chat-small-label">{formatDate(data.createdDate)}</Box>
                <Box className="chat-time chat-small-label">{formatDate(data.createdDate, { format: timeFormat })}</Box>
              </>
            </Grid>
          )}
          <Grid item xs={9}>
            <Box className="talk-bubble tri-right left-top">
              <Box className="talktext">{data.comment}</Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

const renderMyComment = (data: IComment) => {
  return (
    <Box className="main-chat-wrapper-reply">
      <Box>
        <Box className="reply-bubble tri-right right-top">
          <Box className="talktext">{data?.comment}</Box>
        </Box>
      </Box>
    </Box>
  );
};

const renderTimeDaySeparator = (date: string): JSX.Element => {
  return (
    <Box className="day-separator">
      <b>{formatDate(date, { format: dateFormatWithTime })}</b>
    </Box>
  );
};

const groupCommentsByDate = (comments: IComment[]): Dictionary<IComment[]> => {
  return _.groupBy(comments, function(data) {
    moment(data.createdDate).format(dateFormat);
  });
};

export const LenderCommentsModal = ({ onClose, lenderModalPosition }: ILenderCommentsModalProps) => {
  const { userId } = useUserAccount();

  const [searchQuery, setSearchQuery] = useState('');
  const [comment, setComment] = useState('');
  const [groupedComments, setGroupedComments] = useState<Dictionary<IComment[]>>({});
  const [filteredComments, setFilteredComments] = useState<IComment[]>([]);
  const params = queryParams();
  const bottomRef = useRef<null | HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { data: comments = [] } = useSWR<IComment[]>(
    `${SERVICES.DEAL}/lendercomment/deals/${params?.dealId}/lendercomments-all`,
  );

  useEffect(() => {
    if (comments) {
      if (filteredComments.length) {
        setGroupedComments(groupCommentsByDate(filteredComments));
      } else {
        setGroupedComments(groupCommentsByDate(comments));
      }
      setIsLoading(false);
    }
  }, [comments, filteredComments]);

  useEffect(() => {
    bottomRef?.current?.scrollIntoView();
  });

  const renderComments = (): JSX.Element[] => {
    const res: JSX.Element[] = [];
    const commts = Object.keys(groupedComments);

    commts.forEach((date: string) => {
      const dayComments: IComment[] = groupedComments[date];

      res.push(renderTimeDaySeparator(dayComments[0].createdDate));

      dayComments.forEach((c: IComment) => {
        const comment = c.createdBy === userId ? renderMyComment(c) : renderTheirComment(c);
        res.push(comment);
      });
    });

    return res;
  };

  const sendCommentHandler = async() => {
    setIsLoading(true);

    const data = {
      contactId: params.id,
      lenderCode: 'test',
      dealId: params.dealId,
      comment,
      isLenderComment: true,
    };

    await sendLenderCommnet(data);
    setComment('');
    setIsLoading(false);
  };

  const handleOnSearch = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(value);
    setFilteredComments(comments.filter((item: IComment) => item.comment.includes(value)));
  };

  return (
    <>
      <StyleCommentchat />
      <Modal 
        open
        hideBackdrop
        resetPosition={lenderModalPosition}
        title="Lender Comments"
        dialogWidth="1350px" 
        onClose={onClose}
      >
        <Stack>
          <LenderCommentInput
            placeholder="Search"
            value={searchQuery}
            onChange={handleOnSearch}
          />
          <ILendingLoaderWrapper>
            {isLoading ? (
              <ILendingLoader />
            ) : (
              <>
                {renderComments()}
                <div ref={bottomRef} />
              </>
            )}
          </ILendingLoaderWrapper>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
            <Grid item xs={11}>
              <TextInput
                sx={{ mb: 2, px: 2 }}
                placeholder="Type a message"
                value={comment}
                onChange={({ target: { value } }) => setComment(value)}
              />
            </Grid>
            <Grid item xs={1}>
              <IconButton
                disabled={!comment}
                sx={{ marginBottom: '25px', padding: '0px' }}
                onClick={sendCommentHandler}
              >
                <ArrowUpwardIcon disable={!comment} />
              </IconButton>
            </Grid>
          </Grid>
        </Stack>
      </Modal>
    </>
  );
};
